import { Check } from "lucide-react";
import { type ImportStage } from "./useCsvImport";

interface StepIndicatorProps {
  stage: ImportStage;
}

const STEPS = [
  { label: "Upload" },
  { label: "Process" },
  { label: "Result" },
] as const;

/**
 * Maps an ImportStage to a 0-based step index for the progress indicator.
 * @param stage - The current import stage
 * @returns Step index: 0 = upload, 1 = processing, 2 = result
 */
function stageToStepIndex(stage: ImportStage): number {
  switch (stage) {
    case "idle":
      return 0;
    case "uploading":
    case "processing":
      return 1;
    case "complete":
    case "failed":
      return 2;
  }
}

/**
 * Displays a 3-step horizontal progress indicator for the CSV import workflow.
 * @param stage - The current import stage from useCsvImport
 */
export default function StepIndicator({ stage }: StepIndicatorProps) {
  const activeIndex = stageToStepIndex(stage);

  return (
    <div className="flex items-start">
      {STEPS.map((step, i) => {
        const isDone = i < activeIndex;
        const isActive = i === activeIndex;

        return (
          <div key={step.label} className="flex items-start">
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                  isDone
                    ? "bg-primary text-primary-foreground"
                    : isActive
                      ? "bg-stone-900 text-white"
                      : "bg-stone-100 text-stone-400"
                }`}
              >
                {isDone ? <Check className="h-3 w-3" /> : String(i + 1)}
              </div>
              <span
                className={`text-[10px] font-semibold tracking-widest uppercase transition-colors ${
                  isActive
                    ? "text-stone-900"
                    : isDone
                      ? "text-primary"
                      : "text-stone-400"
                }`}
              >
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={`w-14 h-px mx-2 mt-3 flex-shrink-0 transition-colors ${
                  isDone ? "bg-primary" : "bg-stone-200"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
